import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout, DonationButton, useAuth } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

/**
 * Admin dashboard — landing page after login.
 * Displays a welcome hero, quick action cards, and the donation button.
 *
 * Requirements: 27.1, 27.3, 27.4
 */

const heroStyle: CSSProperties = {
  background: 'linear-gradient(135deg, var(--color-brand-green, #1B4332), var(--color-brand-green-light, #2D6A4F))',
  borderRadius: 'var(--radius-card, 12px)',
  padding: 'var(--space-xl, 32px)',
  color: '#FFFFFF',
  marginBottom: 'var(--space-lg, 24px)',
};

const cardStyle: CSSProperties = {
  background: 'var(--color-bg-surface, #FFFFFF)',
  borderRadius: 'var(--radius-card, 12px)',
  boxShadow: 'var(--shadow-card, 0 2px 8px rgba(0,0,0,0.08))',
  padding: 'var(--space-lg, 24px)',
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  transition: 'box-shadow 0.2s',
};

const cardGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 'var(--space-md, 16px)',
  marginBottom: 'var(--space-lg, 24px)',
};

const cardTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-heading-sm, 1.25rem)',
  fontWeight: 'var(--font-weight-semibold, 600)',
  margin: '0 0 var(--space-sm, 8px)',
  color: 'var(--color-brand-green, #1B4332)',
};

const cardDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-body-md, 1rem)',
  color: 'var(--color-text-secondary, #6B7280)',
  margin: 0,
  lineHeight: 1.5,
};

const cardIconStyle: CSSProperties = {
  fontSize: '2rem',
  marginBottom: 'var(--space-sm, 8px)',
};

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const apiClient = useApiClient();
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .request<{ account: { name: string } }>('GET', '/account')
      .then((data) => {
        if (!cancelled) setDisplayName(data.account.name);
      })
      .catch(() => {
        // Non-critical: fall back to email below.
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const greetingName = displayName ?? user?.email ?? null;

  return (
    <AppLayout footer={<DonationButton />}>
      {/* Hero section */}
      <section style={heroStyle} aria-label="Welcome">
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--font-size-heading-lg, 1.75rem)',
            fontWeight: 'var(--font-weight-semibold, 600)',
            lineHeight: 1.2,
          }}
        >
          Welcome back{greetingName ? `, ${greetingName}` : ''}
        </h1>
        <p
          style={{
            margin: 'var(--space-sm, 8px) 0 0',
            fontSize: 'var(--font-size-body-lg, 1.125rem)',
            opacity: 0.9,
            lineHeight: 1.5,
          }}
        >
          Manage your events, teams, and scoring from here.
        </p>
      </section>

      {/* Quick action cards */}
      <nav aria-label="Quick actions" style={cardGridStyle}>
        <Link to="/events" style={cardStyle}>
          <div style={cardIconStyle} aria-hidden="true">📋</div>
          <h2 style={cardTitleStyle}>Manage Events</h2>
          <p style={cardDescStyle}>
            View, edit, and manage all your events and their lifecycle.
          </p>
        </Link>

        <Link to="/events/new" style={cardStyle}>
          <div style={cardIconStyle} aria-hidden="true">➕</div>
          <h2 style={cardTitleStyle}>Create Event</h2>
          <p style={cardDescStyle}>
            Set up a new event with stations, teams, and participants.
          </p>
        </Link>

        <div style={{ ...cardStyle, opacity: 0.7, cursor: 'default' }}>
          <div style={cardIconStyle} aria-hidden="true">📊</div>
          <h2 style={cardTitleStyle}>Leaderboards</h2>
          <p style={cardDescStyle}>
            View live scores and standings for your active events.
          </p>
          <span
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-sm, 8px)',
              fontSize: 'var(--font-size-body-sm, 0.875rem)',
              fontWeight: 'var(--font-weight-semibold, 600)',
              color: 'var(--color-brand-orange, #F48C06)',
            }}
          >
            Coming soon
          </span>
        </div>
      </nav>
    </AppLayout>
  );
}
